"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  Book,
  Bookmark,
  Compass,
  BarChart2,
  Headphones,
  Library,
  Moon,
  Sun,
  User,
  type LucideIcon,
} from "lucide-react";

import { useAppTheme } from "@/components/AppThemeProvider";

// Fixed left sidebar navigation. Rendered out of document flow (position:fixed)
// so each host page only needs a matching left padding (pl-16 md:pl-56) to make
// room — see the page wrappers. Collapses to an icon rail on small screens.

interface NavItem {
  href: string;
  label: string;
  icon: LucideIcon;
  /** When true, only the exact path is active (avoids /library matching
   *  /library/saved). */
  exact?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { href: "/library", label: "Library", icon: Library, exact: true },
  { href: "/lessons", label: "Lessons", icon: Headphones },
  { href: "/library/saved", label: "Saved", icon: Bookmark },
  { href: "/discover", label: "Discover", icon: Compass },
  { href: "/progress", label: "Progress", icon: BarChart2 },
  { href: "/account", label: "Account", icon: User },
];

function isActive(pathname: string, item: NavItem): boolean {
  if (item.exact) return pathname === item.href;
  return pathname === item.href || pathname.startsWith(`${item.href}/`);
}

const NavBar = () => {
  const pathname = usePathname() ?? "";
  const { theme, toggleTheme } = useAppTheme();

  return (
    <aside className="fixed inset-y-0 left-0 z-50 flex w-16 flex-col bg-app-nav-bg shadow-lg md:w-56">
      {/* Brand */}
      <Link
        href="/library"
        className="flex items-center gap-2 px-4 py-4 text-app-nav-fg"
      >
        <Book className="h-6 w-6 shrink-0" />
        <span className="hidden text-lg font-semibold md:inline">Lumi</span>
      </Link>

      {/* Nav links */}
      <nav className="mt-2 flex flex-1 flex-col gap-1 px-2">
        {NAV_ITEMS.map((item) => {
          const active = isActive(pathname, item);
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              href={item.href}
              aria-current={active ? "page" : undefined}
              title={item.label}
              className={`flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors duration-200 ${
                active
                  ? "bg-white/10 text-app-nav-fg"
                  : "text-app-nav-muted hover:bg-white/5 hover:text-app-nav-hover"
              }`}
            >
              <Icon className="h-5 w-5 shrink-0" />
              <span className="hidden text-sm font-medium md:inline">
                {item.label}
              </span>
            </Link>
          );
        })}
      </nav>

      {/* Theme toggle — Paper / Ink */}
      <div className="border-t border-white/10 px-2 py-3">
        <button
          type="button"
          onClick={toggleTheme}
          title={theme === "paper" ? "Switch to Ink" : "Switch to Paper"}
          aria-label={
            theme === "paper" ? "Switch to Ink theme" : "Switch to Paper theme"
          }
          className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-app-nav-muted transition-colors duration-200 hover:bg-white/5 hover:text-app-nav-hover"
        >
          {theme === "paper" ? (
            <Moon className="h-5 w-5 shrink-0" />
          ) : (
            <Sun className="h-5 w-5 shrink-0" />
          )}
          <span className="hidden text-sm font-medium md:inline">
            {theme === "paper" ? "Ink" : "Paper"}
          </span>
        </button>
      </div>
    </aside>
  );
};

export default NavBar;
